/**
 * **매장 PG 설정 — PG사별 MID·라이센스 키, 한 곳.** 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 매장은 PG사를 하나 고르고 MID·라이센스 키는 PG사마다 따로 갖는다(서버 `StorePgKeys` — 고른 PG사의 값은 `tbl_store`, 고르지 않은
 * PG사의 값은 보관 표). 수정 화면 응답은 고른 PG사의 값을 `mid`·`merchantKey` 로, 고르지 않은 PG사의 값을 `storedPgKeys` 로 싣는다.
 * 화면은 둘을 합친 **장부**([pgKeyBook] — 폼의 `pgKeyBook`)로 PG사를 바꿀 때 그 PG사의 값을 채우고([pgSelection]), 저장하지 않은
 * 입력이 있는지 본다([pgKeysDirty]). 저장 단위는 고른 PG사 하나다 — 서버가 PG사를 바꾼 저장에서 내려놓는 PG사의 값을 보관한다.
 *
 * **미사용**(빈 값·`null`)은 PG 가 없는 매장이다. 화면은 MID·라이센스 키 칸을 잠그고 **비워 그리기만** 한다 — 폼 값은 건드리지
 * 않는다. 서버는 미사용 저장에서 두 칸을 바꾸지 않고(`StorePgKeys.planSave`), 이 기능을 모르는 옛 서버를 만나도(배포 순서) 읽은 값을
 * 그대로 돌려보내 남은 MID 를 지우지 않는다 — 같은 PG사로 되돌리면 그 값으로 다시 켠다(콜팝·플레이스 참여 판정은 미사용이면 MID 가 남아도 미달).
 */

/** PG사 선택지 — 값은 `tbl_store.pg_kind`(서버 `PgType` 의 결제 PG 넷), 첫 줄이 미사용이다 */
export const PG_KIND_OPTIONS = Object.freeze([
  Object.freeze({ value: '', label: '미사용' }),
  Object.freeze({ value: 'INNOPAY', label: '이노페이' }),
  Object.freeze({ value: 'HECTO', label: '헥토파이낸셜' }),
  Object.freeze({ value: 'NICEPAY', label: '나이스페이' }),
  Object.freeze({ value: 'KORPAY', label: '코페이' }),
])

/** 수정 화면 응답의 고르지 않은 PG사 저장값 칸 — 서버 `StoredPgKeysView.KEY` */
export const STORED_PG_KEYS = 'storedPgKeys'

/** 화면 문구 — 저장하지 않은 입력을 두고 PG사를 바꿀 때 */
export const PG_KEY_TEXT = Object.freeze({
  discardTitle: '저장하지 않은 값이 있습니다',
  discardMessage: '입력한 MID·라이센스 키를 저장하지 않고 PG사를 바꾸면 입력한 내용이 사라집니다. 바꿀까요?',
  discardConfirm: '바꾸기',
  discardCancel: '계속 수정',
})

/** 두 칸 */
const KEY_FIELDS = Object.freeze(['mid', 'merchantKey'])

/**
 * 고른 PG사 — 빈 값·`null`·`undefined` 는 미사용(`''`).
 *
 * @param {*} value 폼·응답의 `pgKind`
 * @returns {string}
 */
export function pgKindOf(value) {
  return value ? String(value) : ''
}

/** 한 PG사의 두 칸(새 객체) — 없는 칸은 빈칸 */
function keysOf(source) {
  const keys = source || {}

  return { mid: keys.mid || '', merchantKey: keys.merchantKey || '' }
}

/**
 * 수정 화면 응답 → **PG사별 저장값 장부** `{ [pgKind]: {mid, merchantKey} }`(새 객체).
 *
 * 고르지 않은 PG사의 보관값(`storedPgKeys`)에 고른 PG사의 값(`mid`·`merchantKey`)을 얹는다. 미사용 매장의 두 칸은 어느 PG사의 값도
 * 아니라 싣지 않는다(미사용으로 바꾸기 전 PG사의 값은 보관값에 있다).
 *
 * @param {?object} detail `GET /store/{cd}`·`GET /store/info` 응답
 * @returns {object}
 */
export function pgKeyBook(detail) {
  const source = detail || {}
  const stored = source[STORED_PG_KEYS] || {}
  const book = Object.keys(stored).reduce(
    (entries, pgKind) => Object.assign(entries, { [pgKind]: keysOf(stored[pgKind]) }),
    {}
  )
  const pgKind = pgKindOf(source.pgKind)

  return pgKind ? Object.assign(book, { [pgKind]: keysOf(source) }) : book
}

/**
 * 장부의 그 PG사 값 — 저장한 적이 없으면 빈칸(다른 PG사의 값이 따라오지 않는다).
 *
 * @param {?object} book [pgKeyBook]
 * @param {*} pgKind
 * @returns {{mid: string, merchantKey: string}}
 */
export function pgKeysOf(book, pgKind) {
  const kind = pgKindOf(pgKind)

  return keysOf(kind && book ? book[kind] : null)
}

/**
 * PG사를 고른 뒤의 폼 칸 — 그 PG사의 저장값을 채운다. 미사용은 PG사만 바꾸고 두 칸은 건드리지 않는다(화면이 비워 그린다).
 *
 * @param {?object} book [pgKeyBook]
 * @param {*} pgKind 새로 고른 PG사
 * @returns {{pgKind: string, mid?: string, merchantKey?: string}}
 */
export function pgSelection(book, pgKind) {
  const kind = pgKindOf(pgKind)

  return kind ? Object.assign({ pgKind: kind }, pgKeysOf(book, kind)) : { pgKind: kind }
}

/**
 * 고른 PG사의 입력이 저장값과 다른가 — PG사를 바꾸면 사라지는 입력이다. 미사용은 두 칸이 잠겨 있어 늘 아니다.
 *
 * @param {?object} form 매장 폼
 * @param {?object} book [pgKeyBook]
 * @returns {boolean}
 */
export function pgKeysDirty(form, book) {
  const source = form || {}
  const saved = pgKeysOf(book, source.pgKind)

  return !!pgKindOf(source.pgKind) && KEY_FIELDS.some((field) => (source[field] || '') !== saved[field])
}

/**
 * 저장이 끝난 뒤의 장부(새 객체) — 저장한 PG사의 값이 그 PG사의 저장값이 된다. 미사용 저장은 장부를 바꾸지 않는다.
 *
 * @param {?object} book [pgKeyBook]
 * @param {?object} form 저장한 폼
 * @returns {object}
 */
export function pgKeyBookAfterSave(book, form) {
  const source = form || {}
  const pgKind = pgKindOf(source.pgKind)
  const next = Object.assign({}, book)

  return pgKind ? Object.assign(next, { [pgKind]: keysOf(source) }) : next
}

/**
 * 수정 화면 응답 → 폼에 얹을 PG 칸(새 객체) — 장부를 만들고 응답의 보관값 칸은 내린다(장부가 대신한다). MID·라이센스 키는 응답
 * 그대로다 — 미사용 매장도 비우지 않는다(화면이 비워 그린다).
 *
 * @param {?object} detail 수정 화면 응답
 * @returns {{pgKeyBook: object, storedPgKeys: undefined}}
 */
export function pgKeyFormFields(detail) {
  return { pgKeyBook: pgKeyBook(detail), [STORED_PG_KEYS]: undefined }
}
